import { Intent, MenuItem } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ItemRenderer, MultiSelect } from "@blueprintjs/select";
import useApplyVoltages from "contexts/MapViewConfigurationContext/hooks/useApplyVoltages";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useScreeningViewContext } from "contexts/ScreeningViewContext/ScreeningViewContext";
import React, { useMemo } from "react";
import { REGIONS_TO_VOLTAGES, Voltage } from "types/voltageTypes";
import "./VoltageSelectWrapper.scss";

const SELECT_ALL = "SELECT_ALL";
type VoltageItem = Voltage | typeof SELECT_ALL;

const VoltageSelectWrapper: React.FC = () => {
    const {
        busFiltersConfiguration: { busFilters }
    } = useMapViewConfiguration();
    const { voltages } = busFilters;
    const applyVoltages = useApplyVoltages("voltage_dropdown");
    const { region } = useMapViewRoutingMetadata();
    const maybeScreeningViewData = useScreeningViewContext();

    const regionVoltages = REGIONS_TO_VOLTAGES[region];

    const selectableVoltages = useMemo(
        () =>
            maybeScreeningViewData
                ? regionVoltages.filter((v) =>
                      maybeScreeningViewData.voltages.includes(v)
                  )
                : regionVoltages,
        [regionVoltages, maybeScreeningViewData]
    );

    const allSelected =
        selectableVoltages.length > 0 &&
        selectableVoltages.every((v) => voltages.includes(v));

    const items: ReadonlyArray<VoltageItem> = [SELECT_ALL, ...regionVoltages];

    const voltageRenderer: ItemRenderer<VoltageItem> = (
        item,
        { handleClick, modifiers }
    ) => {
        if (!modifiers.matchesPredicate) {
            return null;
        }

        if (item === SELECT_ALL) {
            return (
                <MenuItem
                    active={modifiers.active}
                    key={SELECT_ALL}
                    text="Select All"
                    onClick={handleClick}
                    role="listoption"
                    icon={allSelected ? IconNames.SMALL_TICK : IconNames.BLANK}
                    disabled={selectableVoltages.length === 0}
                />
            );
        }

        return (
            <MenuItem
                active={modifiers.active}
                key={item}
                text={getVoltageLabel(item)}
                onClick={handleClick}
                role="listoption"
                icon={
                    voltages.includes(item)
                        ? IconNames.SMALL_TICK
                        : IconNames.BLANK
                }
                disabled={
                    maybeScreeningViewData &&
                    !maybeScreeningViewData.voltages.includes(item)
                }
            />
        );
    };

    const handleItemSelect = (item: VoltageItem) => {
        if (item === SELECT_ALL) {
            applyVoltages(allSelected ? [] : selectableVoltages);
            return;
        }

        const next = voltages.includes(item)
            ? voltages.filter((v) => v !== item)
            : [...voltages, item];
        applyVoltages(next);
    };

    const removeVoltage = (item: VoltageItem) => {
        if (item === SELECT_ALL) {
            return;
        }
        applyVoltages(voltages.filter((v) => v !== item));
    };

    return (
        <MultiSelect<VoltageItem>
            className="VoltageSelectWrapper-select"
            popoverProps={{ minimal: true }}
            items={[...items]}
            selectedItems={[...voltages]}
            fill={true}
            onClear={() => applyVoltages([])}
            tagRenderer={(item) => {
                if (item === SELECT_ALL) {
                    return "";
                }
                return `${item} kV`;
            }}
            itemRenderer={voltageRenderer}
            onItemSelect={handleItemSelect}
            tagInputProps={{
                tagProps: { minimal: true, intent: Intent.PRIMARY }
            }}
            onRemove={removeVoltage}
        />
    );
};

const getVoltageLabel = (voltage: Voltage): string => {
    return `${voltage} kV`;
};

export default VoltageSelectWrapper;
